/** TanStack Query hooks for Wiki Health (report, dismiss, fixes) and Ask */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AskResult, HealthAutoFixResult, HealthFindingType, HealthReport, WikiWriteResult } from '@agentwiki/shared'
import { apiClient } from '../lib/api-client'
import { projectKeys } from './use-projects'

const contentKey = (project: string) => [...projectKeys.all, 'content', project] as const
const healthKey = (project: string) => [...contentKey(project), 'health'] as const

function projectPath(project: string, suffix = '') {
  return `/api/projects/${encodeURIComponent(project)}${suffix}`
}

export interface HealthParams {
  type?: HealthFindingType
  staleDays: number
  includeDismissed: boolean
  limit: number
  offset: number
}

export function useHealthReport(project: string, params: HealthParams) {
  const qs = new URLSearchParams({ staleDays: String(params.staleDays), limit: String(params.limit), offset: String(params.offset) })
  if (params.type) qs.set('type', params.type)
  if (params.includeDismissed) qs.set('includeDismissed', 'true')
  return useQuery({
    queryKey: [...healthKey(project), params],
    queryFn: () => apiClient.get<HealthReport>(projectPath(project, `/health?${qs}`)),
    enabled: Boolean(project),
    placeholderData: (previous) => previous,
  })
}

export type HealthActionInput = { action: 'dismiss' | 'restore' | 'fix'; key: string } | { action: 'fix-certain' }
export type HealthActionResult = WikiWriteResult | HealthAutoFixResult | { key: string; dismissed: boolean }

/** Dismiss, restore, propose one fix, or propose every certain fix; refreshes Health, the wiki, and the queue */
export function useHealthAction(project: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: HealthActionInput): Promise<HealthActionResult> => {
      if (input.action === 'fix-certain') return apiClient.post<HealthAutoFixResult>(projectPath(project, '/health/fix-certain'), {})
      if (input.action === 'fix') return apiClient.post<WikiWriteResult>(projectPath(project, '/health/fix'), { key: input.key })
      return apiClient.post<{ key: string; dismissed: boolean }>(projectPath(project, `/health/${input.action}`), { key: input.key })
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: contentKey(project) }),
  })
}

/** Ask a question; the request id makes a retried submit return the first answer instead of charging again */
export function useAsk(project: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { question: string; requestId: string; budget?: number }) => apiClient.post<AskResult>(projectPath(project, '/ask'), input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['credits'] })
      void queryClient.invalidateQueries({ queryKey: healthKey(project) })
    },
  })
}

/** Save an answered question to the wiki as a saved question (a proposal; no credits); refreshes the wiki and the queue */
export function useSaveAsk(project: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (askId: string) => apiClient.post<WikiWriteResult>(projectPath(project, `/ask/${encodeURIComponent(askId)}/save`), {}),
    onSettled: () => queryClient.invalidateQueries({ queryKey: contentKey(project) }),
  })
}
